import { getAPIURL } from "./config";

const appendJournalFilters = (
  params,
  {
    dateFrom,
    dateTo,
    account,
    organization,
    q,
    sourceSystems,
    analytics,
    analyticLayer,
    hasCorr,
    hasIssues,
    excluded,
    ruleId,
  }
) => {
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  if (account) params.set("account", account);
  if (organization) params.set("organization", organization);
  if (q) params.set("q", q);
  (sourceSystems || []).forEach((value) => params.append("source_system", value));
  (analytics || []).forEach((value) => params.append("analytic", value));
  if (analyticLayer) params.set("analytic_layer", analyticLayer);
  if (hasCorr != null) params.set("has_corr", String(hasCorr));
  if (hasIssues != null) params.set("has_issues", String(hasIssues));
  if (excluded != null) params.set("excluded", String(excluded));
  if (ruleId) params.set("rule_id", ruleId);
};

const downloadBlob = (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const parseDownloadFilename = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;

  const encoded = contentDisposition.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/i);
  if (encoded?.[1]) {
    const raw = encoded[1].trim().replace(/^["']|["']$/g, "");
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  const plain = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
  if (plain?.[1]) {
    const raw = plain[1].replace(/['"]/g, "").trim();
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  return fallback;
};

const isZip = (bytes) => bytes?.length > 1 && bytes[0] === 0x50 && bytes[1] === 0x4b;

/** Ответ может быть файлом XLSX или JSON-строкой с base64 (так описана схема). */
const toExcelBlob = (buffer) => {
  const bytes = new Uint8Array(buffer);
  if (isZip(bytes)) {
    return new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  }

  const text = new TextDecoder().decode(bytes).trim();
  let payload = text;
  if (text.startsWith('"') || text.startsWith("{")) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (typeof payload === "string") {
    const base64 = payload.replace(/^data:.*?;base64,/, "").replace(/\s/g, "");
    try {
      const binary = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
      if (isZip(binary)) {
        return new Blob([binary], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }
    } catch {
      // не base64
    }
  }

  return null;
};

const readErrorMessage = async (response, fallback) => {
  const errorText = await response.text();
  if (response.status === 422) {
    try {
      const err = JSON.parse(errorText);
      const details = Array.isArray(err.detail)
        ? err.detail.map((item) => item.msg || JSON.stringify(item)).join("; ")
        : err.detail;
      if (details) return `Ошибка валидации: ${details}`;
    } catch {
      if (errorText) return `${fallback}. ${errorText}`;
    }
  }
  return errorText ? `${fallback}. ${errorText}` : fallback;
};

/**
 * Журнал операций: каждая проводка со слоями source / corr / mgmt.
 * GET /finance/journal
 * Без дат бэкенд отдаёт месяц последней проводки.
 * @param {{ dateFrom?: string, dateTo?: string, account?: string, organization?: string, q?: string, sourceSystems?: string[], analytics?: string[], analyticLayer?: "source"|"mgmt", hasCorr?: boolean, hasIssues?: boolean, excluded?: boolean, ruleId?: string, limit?: number, offset?: number, signal?: AbortSignal }} params
 * dateFrom / dateTo — YYYY-MM-DD
 */
export const getFinanceJournal = async ({
  dateFrom,
  dateTo,
  account,
  organization,
  q,
  sourceSystems,
  analytics,
  analyticLayer,
  hasCorr,
  hasIssues,
  excluded,
  ruleId,
  limit = 50,
  offset = 0,
  signal,
} = {}) => {
  const params = new URLSearchParams();
  appendJournalFilters(params, {
    dateFrom,
    dateTo,
    account,
    organization,
    q,
    sourceSystems,
    analytics,
    analyticLayer,
    hasCorr,
    hasIssues,
    excluded,
    ruleId,
  });
  params.set("limit", String(limit));
  params.set("offset", String(offset));

  const response = await fetch(`${getAPIURL()}finance/journal?${params}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
    signal,
  });

  if (!response.ok) {
    const fallback = `Ошибка загрузки журнала операций: ${response.status} ${response.statusText}`;
    throw new Error(await readErrorMessage(response, fallback));
  }

  return response.json();
};

/**
 * Экспорт журнала в XLSX, все строки по фильтрам, без пагинации.
 * GET /finance/journal/excel
 * Фильтры те же, что у GET /finance/journal. Без дат — месяц последней проводки.
 * @param {{ dateFrom?: string, dateTo?: string, account?: string, organization?: string, q?: string, signal?: AbortSignal }} params
 * dateFrom / dateTo — YYYY-MM-DD
 */
export const exportFinanceJournalExcel = async ({
  dateFrom,
  dateTo,
  account,
  organization,
  q,
  signal,
} = {}) => {
  const params = new URLSearchParams();
  appendJournalFilters(params, { dateFrom, dateTo, account, organization, q });
  const query = params.toString();

  const response = await fetch(
    `${getAPIURL()}finance/journal/excel${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, application/json",
      },
      credentials: "include",
      signal,
    }
  );

  if (!response.ok) {
    const fallback = `Ошибка выгрузки журнала: ${response.status} ${response.statusText}`;
    throw new Error(await readErrorMessage(response, fallback));
  }

  const blob = toExcelBlob(await response.arrayBuffer());
  if (!blob) {
    throw new Error("Сервер вернул не файл Excel");
  }

  const fallbackName = ["Журнал_операций", dateFrom, dateTo]
    .filter(Boolean)
    .join("_")
    .concat(".xlsx");
  const filename = parseDownloadFilename(
    response.headers.get("content-disposition"),
    fallbackName
  );
  downloadBlob(blob, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
};
